import React from 'react'
import ReactECharts from 'echarts-for-react';
import { Skeleton, Grid } from 'antd';

/**
 * onCategoryClick：把「繪圖區內的任何一點」換算成分類索引後回呼。
 *
 * echarts 的 series click 只有打到圖元本身才觸發。長條圖在窄螢幕上柱寬會掉到幾個像素
 * (實測 390px 下訂房增速只有 8px、前瞻圖 2.7px)，值為 0 的柱更是完全沒有圖元 ——
 * 實測手機上點擊成功率是 0%。
 *
 * 曾經改用「疊一層透明的整欄長條」解決，但那會壞掉版面：barWidth:'100%' 會霸佔整個
 * 分類槽，實測讓真正的柱子從 10px 被擠成 1px，而且換前後順序也無效。
 * 改走 zrender 的點擊 + convertFromPixel，對渲染零影響。
 */
function useCategoryClick(ref, hasOption, onCategoryClick) {
    React.useEffect(() => {
        if (!onCategoryClick || !hasOption || !ref.current) return;
        const inst = ref.current.getEchartsInstance && ref.current.getEchartsInstance();
        if (!inst) return;
        const zr = inst.getZr();
        const handler = (e) => {
            const p = [e.offsetX, e.offsetY];
            // 只認繪圖區內的點擊，避免點到圖例或軸標籤也觸發
            if (!inst.containPixel('grid', p)) return;
            const coord = inst.convertFromPixel({ seriesIndex: 0 }, p);
            const idx = coord && coord[0];
            if (idx == null || Number.isNaN(idx)) return;
            // 開抽屜前主動關掉 tooltip。echarts 的 tooltip div 寫死 z-index: 9999999
            // (echarts/lib/component/tooltip/TooltipHTMLContent.js)，而 antd Drawer 是
            // 1000，所以 tooltip 必然浮在抽屜之上；而 echarts 隱藏它靠的是指標離開圖表
            // (mouseout/mouseleave)，觸控上永遠不會發生 —— 於是手機點一下開抽屜之後，
            // tooltip 就留在畫面上擋著抽屜內容，而且沒有任何東西會去關它。
            inst.dispatchAction({ type: 'hideTip' });
            onCategoryClick(Math.round(idx));
        };
        zr.on('click', handler);
        // 卸載時 echarts-for-react 會先在 componentWillUnmount dispose 圖表，之後才輪到這裡的
        // effect cleanup；此時 zrender 的 handler 已被設成 null，直接 off() 會爆 TypeError。
        return () => { if (!inst.isDisposed()) zr.off('click', handler); };
    }, [ref, hasOption, onCategoryClick]);
}

/**
 * canvas 對螢幕閱讀器等同一張沒有 alt 的圖片 —— echarts 的 aria.enabled 預設是 false，
 * 不開的話整張圖在無障礙樹上完全不存在 (WCAG 1.1.1)。
 *
 * 開了之後 echarts 會依資料自動組一句描述掛上 aria-label；呼叫端若給得出更好的摘要
 * (例如「未來 90 天平均在手率 42%，最低 10/14 12%」) 就用 ariaLabel 覆寫。
 *
 * 刻意不提供 decal (紋理) 開關：echarts 的 aria.decal 是整張圖生效的
 * (visual/aria.js 的 setDecal 走 eachRawSeries)，沒辦法只套長條而放過同一張圖裡的
 * 面積系列 —— 前瞻圖的「近年正常帶」是帶 areaStyle 的 line，開了就會被打上網點，
 * 那條帶正是判斷超前/落後的參考基準，糊掉比沒有紋理更糟。
 * 要補「不單靠顏色」的通道，用柱頂符號或表格檢視，不要用 decal。
 */
function withAria(option, ariaLabel) {
    if (!option) return option;
    const aria = { enabled: true };
    if (ariaLabel) aria.label = { enabled: true, description: ariaLabel };
    return { ...option, aria: { ...aria, ...(option.aria || {}) } };
}

export default function ResponsiveECharts({ style, onCategoryClick, ariaLabel, ...props }) {
    const screens = Grid.useBreakpoint();
    const ref = React.useRef(null);
    let newStyle = { width: '100%', ...style };
    // xs, sm, md, lg, xl, xxl
    Object.keys(screens).forEach((screenSizeName) => {
        if (props[screenSizeName] && screens[screenSizeName] ) {
            newStyle.height = props[screenSizeName];
        }
    })
    // 容器尺寸變動時重算圖表大小 (echarts 預設只監聽 window resize，Row/Col 版面變動不會觸發，
    // 會導致 canvas 用到錯誤寬度而超出卡片)。只在有/無 option 切換時掛/卸 observer。
    const hasOption = Boolean(props.option);
    React.useEffect(() => {
        if (!hasOption || !ref.current) return;
        const inst = ref.current.getEchartsInstance && ref.current.getEchartsInstance();
        const el = ref.current.ele;
        if (!inst || !el) return;
        const resize = () => { try { inst.resize(); } catch (e) { /* noop */ } };
        resize();
        let ro;
        if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(resize); ro.observe(el); }
        return () => { if (ro) ro.disconnect(); };
    }, [hasOption]);
    useCategoryClick(ref, hasOption, onCategoryClick);
    return (
        <>{props.option ?
            <ReactECharts ref={ref} opts={{ locale: 'zh-tw' }} style={newStyle} {...props}
                option={withAria(props.option, ariaLabel)} /> :
            <Skeleton style={newStyle} />}</>
    )
}
